import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { KeyRound, Euro, CalendarDays, TrendingUp, Loader2, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const eur = (v) => `€${Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
const fmt = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('tr-TR') : '—');

const Kpi = ({ icon: Icon, label, value, sub, tone, testId }) => (
  <div className="rounded-xl border bg-card p-4" data-testid={testId}>
    <div className="flex items-center justify-between text-xs text-muted-foreground">{label}<Icon className={`h-4 w-4 ${tone}`} /></div>
    <div className="text-2xl font-bold mt-1">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
  </div>
);

const plannedCost = (r) => {
  if (!r.rental_start_date || !r.rental_end_date || !r.daily_fee) return null;
  const days = Math.round((new Date(r.rental_end_date) - new Date(r.rental_start_date)) / 86400000) + 1;
  return { days, cost: days * r.daily_fee };
};

const RentalRow = ({ r }) => {
  const plan = plannedCost(r);
  const max = Math.max(...r.months.map((m) => m.cost), 1);
  return (
    <tr className="border-t hover:bg-muted/40" data-testid={`rental-cost-row-${r.vehicle_id}`}>
      <td className="px-3 py-2">
        <Link to={`/dashboard/vehicles/${r.vehicle_id}`} className="font-mono font-bold text-primary hover:underline">{r.license_plate}</Link>
        <div className="text-[11px] text-muted-foreground">{[[r.brand, r.model].filter(Boolean).join(' '), r.firm_name].filter(Boolean).join(' · ')}</div>
      </td>
      <td className="px-3 py-2 font-mono text-xs">{r.replaces_plate || '—'}</td>
      <td className="px-3 py-2 text-xs">{r.rental_company || '—'}</td>
      <td className="px-3 py-2 text-xs whitespace-nowrap">
        {fmt(r.rental_start_date)} → {fmt(r.rental_end_date)}
        <div>{r.active ? <span className="text-emerald-600 font-semibold">Devam ediyor</span> : <span className="text-muted-foreground">Bitti / başlamadı</span>}</div>
      </td>
      <td className="px-3 py-2 text-xs tabular-nums">{r.daily_fee != null ? eur(r.daily_fee) : '—'}</td>
      <td className="px-3 py-2">
        <div className="flex items-end gap-[3px] h-12" title="Aylık maliyet">
          {r.months.map((m, i) => (
            <div key={MONTHS[i]} className="flex flex-col items-center gap-0.5 h-full justify-end">
              <div className={`w-3 rounded-sm ${m.cost ? 'bg-amber-500' : 'bg-muted'}`} style={{ height: `${Math.max(6, (m.cost / max) * 80)}%` }} title={`${MONTHS[i]}: ${m.days} gün · ${eur(m.cost)}`} />
              <span className="text-[8px] leading-none text-muted-foreground">{MONTHS[i][0]}</span>
            </div>
          ))}
        </div>
      </td>
      <td className="px-3 py-2 text-xs tabular-nums"><b>{r.year_days}</b> gün<div className="font-semibold">{eur(r.year_cost)}</div></td>
      <td className="px-3 py-2 text-xs tabular-nums"><b>{r.total_days}</b> gün<div className="font-semibold">{eur(r.total_cost)}</div></td>
      <td className="px-3 py-2 text-xs tabular-nums">
        {plan ? <>{plan.days} gün<div className="font-semibold">{eur(plan.cost)}</div></> : '—'}
        {r.missing_data.length > 0 && <div className="flex items-center gap-1 text-amber-600"><AlertTriangle className="h-3 w-3" />{r.missing_data.join(', ')} eksik</div>}
      </td>
    </tr>
  );
};

export function RentalCostReport() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get('/vehicles/reports/rental-costs', { params: { year } })
      .then((r) => setData(r.data?.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [year]);

  const chart = useMemo(() => (data?.month_totals || []).map((v, i) => ({ month: MONTHS[i], cost: v })), [data]);
  const avgDaily = data?.rows?.length ? data.rows.filter((r) => r.daily_fee).reduce((a, r) => a + r.daily_fee, 0) / (data.rows.filter((r) => r.daily_fee).length || 1) : 0;

  return (
    <div className="space-y-4" data-testid="rental-cost-report">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="font-semibold flex items-center gap-2"><Euro className="h-4 w-4 text-amber-600" />Kiralık Maliyet Raporu</h3>
          <p className="text-xs text-muted-foreground">Maliyet = günlük ücret × o ay kiralı kalınan gün (bugüne kadar)</p>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
          <SelectTrigger className="w-28 h-8" data-testid="rental-cost-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[thisYear - 2, thisYear - 1, thisYear].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {loading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
      {data && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi testId="rental-kpi-active" icon={KeyRound} tone="text-amber-600" label="Şu an kiralık" value={data.active_count} sub={`${data.rows.length} kiralık araç kaydı`} />
            <Kpi testId="rental-kpi-month" icon={CalendarDays} tone="text-sky-600" label="Bu ay maliyet" value={data.current_month_total != null ? eur(data.current_month_total) : '—'} />
            <Kpi testId="rental-kpi-year" icon={TrendingUp} tone="text-rose-600" label={`${year} toplam`} value={eur(data.year_total)} />
            <Kpi testId="rental-kpi-avg" icon={Euro} tone="text-emerald-600" label="Ort. günlük ücret" value={eur(avgDaily)} />
          </div>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Aylık kiralama maliyeti — {year}</CardTitle></CardHeader>
            <CardContent className="h-56" data-testid="rental-cost-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" fontSize={11} />
                  <YAxis fontSize={11} tickFormatter={(v) => `€${v}`} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Bar dataKey="cost" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <div className="rounded-xl border overflow-x-auto">
            <table className="w-full text-sm" data-testid="rental-cost-table">
              <thead className="bg-muted/50 text-[11px] uppercase text-muted-foreground">
                <tr>
                  {['Plaka', 'Yerine geldiği', 'Kiralama firması', 'Süre', 'Günlük', `${year} aylık`, `${year} toplam`, 'Şimdiye kadar', 'Dönüşe kadar planlanan'].map((h) => <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Kiralık araç yok</td></tr>}
                {data.rows.map((r) => <RentalRow key={r.vehicle_id} r={r} />)}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
